import { useSubmit } from "@hyper-fetch/react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { type ChangeEvent, type FormEvent, useCallback, useState } from "react";
import { toast } from "sonner";

import { sdk } from "@/api/sdk";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { m } from "@/paraglide/messages.js";

const MESSAGE_MIN_LENGTH = 10;

const EMPTY_VALUES = { name: "", email: "", company: "", message: "" };

type ContactValues = typeof EMPTY_VALUES;

/**
 * Public contact form. Posts to the unauthenticated `/contact` endpoint, which
 * forwards the submission to the team inbox with the sender as reply-to.
 */
export const ContactForm = () => {
  const [values, setValues] = useState<ContactValues>(EMPTY_VALUES);
  const [sentTo, setSentTo] = useState<{ name: string; email: string } | null>(null);

  const contactResponse = useSubmit(sdk.contact.$post);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setValues((previous) => ({ ...previous, [name]: value }));
  }, []);

  const handleReset = useCallback(() => {
    setSentTo(null);
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const message = values.message.trim();

      if (message.length < MESSAGE_MIN_LENGTH) {
        toast.error(m.contact_error_message_short());
        return;
      }

      const { error } = await contactResponse.submit({
        payload: {
          name: values.name.trim(),
          email: values.email.trim(),
          company: values.company.trim() || undefined,
          message,
        },
      });

      if (error) {
        toast.error(error.message || m.contact_error_generic());
        return;
      }

      setSentTo({ name: values.name.trim(), email: values.email.trim() });
      setValues(EMPTY_VALUES);
    },
    [contactResponse, values],
  );

  if (sentTo) {
    return (
      <div className="flex flex-col items-start gap-4 rounded-2xl border border-border/60 bg-card p-6 md:p-8">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
          <CheckCircle2 className="size-6 text-primary" />
        </div>
        <div className="flex flex-col gap-1.5">
          <h2 className="text-xl font-semibold tracking-tight text-foreground">{m.contact_success_title()}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {m.contact_success_description({ name: sentTo.name, email: sentTo.email })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" onClick={handleReset}>
            {m.contact_success_again()}
          </Button>
          <a href="/" className="text-sm font-medium text-primary underline-offset-4 hover:underline">
            {m.contact_back_home()}
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex h-full flex-col gap-6 rounded-2xl border border-border/60 bg-card p-6 md:p-8"
    >
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="contact-name">{m.contact_field_name_label()}</FieldLabel>
          <Input
            id="contact-name"
            name="name"
            autoComplete="name"
            required
            maxLength={120}
            placeholder={m.contact_field_name_placeholder()}
            value={values.name}
            onChange={handleChange}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="contact-email">{m.contact_field_email_label()}</FieldLabel>
          <Input
            id="contact-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder={m.contact_field_email_placeholder()}
            value={values.email}
            onChange={handleChange}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="contact-company">
            {m.contact_field_company_label()}{" "}
            <span className="font-normal text-muted-foreground">({m.contact_field_company_optional()})</span>
          </FieldLabel>
          <Input
            id="contact-company"
            name="company"
            autoComplete="organization"
            maxLength={160}
            placeholder={m.contact_field_company_placeholder()}
            value={values.company}
            onChange={handleChange}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="contact-message">{m.contact_field_message_label()}</FieldLabel>
          <Textarea
            id="contact-message"
            name="message"
            required
            rows={6}
            maxLength={5000}
            placeholder={m.contact_field_message_placeholder()}
            value={values.message}
            onChange={handleChange}
          />
          <FieldDescription>{m.contact_message_hint()}</FieldDescription>
        </Field>
      </FieldGroup>
      <Button type="submit" className="mt-auto w-full" disabled={contactResponse.submitting}>
        {contactResponse.submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        {m.contact_submit()}
      </Button>
    </form>
  );
};
